import { toast } from 'react-toastify';

const ctxRef = { current: null };

const getCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctxRef.current) ctxRef.current = new AC();
  if (ctxRef.current.state === 'suspended') ctxRef.current.resume().catch(() => {});
  return ctxRef.current;
};

export const unlockKvarxAudio = () => {
  const ctx = getCtx();
  if (ctx?.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  requestKvarxNotifyPermission();
};

const playSequence = (ctx, kind) => {
  if (!ctx || ctx.state !== 'running') return;
  const sequences = {
    job: [
      [880, 0.12],
      [1174, 0.18],
    ],
    accepted: [
      [523, 0.12],
      [659, 0.12],
      [784, 0.14],
      [1046, 0.28],
    ],
    offer: [
      [988, 0.08],
      [988, 0.08],
      [1318, 0.14],
    ],
    gone: [[196, 0.22]],
  };
  let t = ctx.currentTime + 0.01;
  for (const [freq, dur] of sequences[kind] || sequences.job) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.14, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + dur + 0.02);
    t += dur + 0.05;
  }
};

export const playKvarxTone = (kind) => {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().then(() => playSequence(ctx, kind)).catch(() => {});
    return;
  }
  playSequence(ctx, kind);
};

export const buzzKvarx = (pattern = [140, 70, 140]) => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch {
    // ignore
  }
};

let titleTimer = null;
export const flashTitle = (text) => {
  const prev = document.title;
  document.title = text;
  clearTimeout(titleTimer);
  titleTimer = setTimeout(() => {
    document.title = prev;
  }, 6000);
};

export const requestKvarxNotifyPermission = () => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {});
  }
};

export const desktopNotify = (title, body) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const note = new Notification(title, {
      body,
      tag: 'kvarx-serviser',
      renotify: true,
      silent: false,
    });
    window.setTimeout(() => note.close(), 12000);
  } catch {
    // ignore blocked notifications
  }
};

const collectCompanyIds = () => {
  const ids = new Set();
  try {
    const profile = JSON.parse(localStorage.getItem('profile') || '{}');
    const result = profile?.result || {};
    [
      result.companyId,
      result._id,
      result.id,
      localStorage.getItem('currentCompanyId'),
      profile?.companyId,
    ]
      .filter(Boolean)
      .forEach((value) => ids.add(String(value)));
  } catch {
    // ignore
  }
  return ids;
};

export const readCompanyId = () => {
  const ids = [...collectCompanyIds()];
  return ids[0] || '';
};

export const isMyCompany = (companyId) => {
  if (!companyId) return false;
  return collectCompanyIds().has(String(companyId));
};

const toastOpts = (ms = 9000) => ({
  autoClose: ms,
  position: 'top-center',
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  style: { zIndex: 100000 },
});

export const alertNewJob = (job) => {
  unlockKvarxAudio();
  playKvarxTone('job');
  buzzKvarx([100, 50, 100]);
  const code = job?.jobCode ? `#${job.jobCode}` : 'Novi posao';
  const where = [job?.itemLabel || job?.intervention, job?.city].filter(Boolean).join(' · ');
  toast.info(`${code} je objavljen na kvarx.${where ? ` ${where}` : ''}`, {
    ...toastOpts(8000),
    toastId: `kvarx-job-${job?.id || job?._id || Date.now()}`,
  });
  flashTitle('🔔 Novi kvarx posao');
  desktopNotify('Novi kvarx posao', where || 'Otvoren je novi posao.');
};

export const alertOfferAccepted = (mine, jobId = '') => {
  unlockKvarxAudio();
  const key = String(jobId || 'x');
  if (mine) {
    playKvarxTone('accepted');
    buzzKvarx([120, 60, 120, 60, 200]);
    toast.success('🎉 Klijent je prihvatio VAŠU ponudu! Posao je u Ugovorenim.', {
      ...toastOpts(12000),
      toastId: `kvarx-won-${key}`,
    });
    flashTitle('✓ Vaša ponuda je izabrana');
    desktopNotify('Ponuda prihvaćena', 'Klijent je izabrao baš vašu ponudu.');
    return;
  }
  playKvarxTone('gone');
  toast.info('Posao je dogovoren sa drugim serviserom.', {
    ...toastOpts(6000),
    toastId: `kvarx-lost-${key}`,
  });
};

export const alertJobRemoved = () => {
  unlockKvarxAudio();
  playKvarxTone('gone');
  toast.info('Klijent je povukao posao sa kvarx.', toastOpts(6000));
};

export const alertOfferExpired = (mine) => {
  if (!mine) return;
  unlockKvarxAudio();
  playKvarxTone('gone');
  toast.info('Vaša ponuda je istekla.', toastOpts(6000));
};
